import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AttachmentTemplateForm } from '@/app/(client)/orders/-components/AttachmentTemplateForm';
import { useAttachmentTemplateDocumentTypes } from '@/app/(client)/orders/-hooks/useAttachmentTemplateDocumentTypes';
import type { AttachmentTemplateFormValues } from '@/app/(client)/orders/-types';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { queryClient } from '@/lib/utils/queryClient';
import { AttachmentTemplateFormSkeleton } from '../../../-components/AttachmentTemplateFormSkeleton';
import {
  adminAttachmentTemplateDetailQueryOptions,
  useAdminAttachmentTemplateDetail,
} from '../../../-hooks/useAdminAttachmentTemplateDetail';
import { useSaveAttachmentTemplate } from '../../../-hooks/useSaveAttachmentTemplate';
import { toAdminTemplateFormValues } from '../../../-utils/transformers';

export const Route = createFileRoute(
  '/management/attachment-templates/(pages)/$id/edit/',
)({
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(adminAttachmentTemplateDetailQueryOptions(params.id))
      .catch(() => undefined),
  component: EditAttachmentTemplatePage,
});

function EditAttachmentTemplatePage() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({
    page: 'managementAttachmentTemplates',
  });
  const navigate = useNavigate();

  const { data, error, refetch } = useAdminAttachmentTemplateDetail(id);
  const {
    data: fpGroups = [],
    isPending: isFpGroupsPending,
    error: fpGroupsError,
    refetch: refetchFpGroups,
  } = useGroupedFacilityProcessOptions();
  const countryOptions = COUNTRY_OPTIONS;
  const { data: documentTypeOptions = [] } =
    useAttachmentTemplateDocumentTypes();

  const allCountryCodes = countryOptions.map(c => c.key);
  const allFpKeys = (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
    g.options.map(o => o.key),
  );

  const { save, isPending } = useSaveAttachmentTemplate({
    id,
    allCountryCodes,
    // The backup rule's "all remaining" scope is derived from these at save
    // time; without them it would be persisted with no facility processes and
    // could never match an order.
    allFacilityProcessKeys: allFpKeys,
    // Seeded from the record, never assumed. `updateTemplateDb` writes
    // `IS_ACTIVE` on every PATCH and the body schema defaults it to `true`, so
    // omitting it — as the old dashboard did — silently reactivates a template
    // someone had deactivated.
    isActive: data?.isActive ?? true,
  });

  if (error || !data) {
    return <QueryErrorState error={error} onRetry={() => void refetch()} />;
  }

  if (fpGroupsError) {
    return (
      <QueryErrorState
        error={fpGroupsError}
        onRetry={() => void refetchFpGroups()}
      />
    );
  }

  // The form must not mount before the facility-process options arrive.
  // `useForm` snapshots `defaultValues` once, and `toAdminTemplateFormValues`
  // expands a rule stored as "all facility processes" using this list — with an
  // empty list that expansion yields `[]`, so the form would open showing (and
  // on save persist) no facility processes at all. The old page did the same,
  // rendering a loader until the options resolved.
  if (isFpGroupsPending) {
    return <AttachmentTemplateFormSkeleton />;
  }

  const defaultValues: AttachmentTemplateFormValues = toAdminTemplateFormValues(
    data,
    allFpKeys,
    allCountryCodes,
  );

  const handleSubmit = async (values: AttachmentTemplateFormValues) => {
    const result = await save(values);
    if (!result) return;
    void navigate({
      to: '/management/attachment-templates/$id',
      params: { id },
    });
  };

  return (
    <AttachmentTemplateForm
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isPending={isPending}
      submitLabel={t('edit.title')}
      // Gates the Save button and shows the in-use banner; every field stays
      // editable, matching the old page. The real enforcement is the server's
      // 409, which `useSaveAttachmentTemplate` catches — this flag is only the
      // snapshot taken when the form loaded.
      hasOrders={data.hasOrders}
      fpGroups={fpGroups as FacilityProcessGroupOption[]}
      countryOptions={countryOptions}
      documentTypeOptions={documentTypeOptions}
    />
  );
}
